
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import type { PlaidAccountMapBody } from '../../services/api/banking';
import { useToast } from '../../components/common/ToastContext';
import { getFriendlyErrorMessage } from '@repo/shared-utils';
import { ChartOfAccount, Bill, JournalEntry } from '@repo/shared-types';
import { billSchema, paymentSchema, bankAccountSchema, receivableSchema, journalEntrySchema, chartOfAccountSchema, distributionSchema, budgetSchema, bankTransactionSchema, type Distribution, type Budget } from '@repo/shared-types/accounting/schemas';
import { recurringBillSchema } from '@repo/shared-types/accounting/recurringBillSchemas';
import { purchaseOrderSchema, threeWayMatchSchema } from '@repo/shared-types/accounting/purchaseOrderSchemas';
import { statementBatchListResponseSchema } from '@repo/shared-types/accounting/statementBatchSchemas';
import { paymentPlanSchema } from '@repo/shared-types/accounting/paymentPlanSchemas';
import { fileRecordResponseSchema } from '@repo/shared-types/files/schemas';
import { validateEntity, validateEntityArray, validateSuccessResponse } from '@repo/shared-utils';
import {
  parseBankTransactionImportResponse,
  parseBankTransactionMutationResponse,
  parseBillMutationResponse,
  parseDistributionMutationResponse,
  parseReconciliationCompleteResponse,
  parseThreeWayMatchMutationResponse,
} from '../../utils/parseAccountingMutationResponse';
import { z } from 'zod';
import { logger } from '@repo/shared-utils';
import type { RecurringBill, RecurringBillGenerationResult } from '@repo/shared-types/accounting/recurringBills';
import type { PurchaseOrder } from '@repo/shared-types/accounting/purchaseOrders';
import type { } from '@repo/shared-types/accounting/paymentPlans';
import type { InstallmentStatus } from '@repo/shared-types/common/statuses';
import type { AccountingAPI } from '@repo/shared-types/api';
import { newClientEntityId } from '../../utils/ids';

// Typed reference to accounting API to ensure type safety
const accountingAPI = api.accounting as typeof api.accounting & AccountingAPI;

/**
 * React Query hook to fetch all bills.
 * @param filters - Optional server-side filters (propertyId, vendorId, status)
 * @returns Query result containing bills array
 */
export const useBills = (filters?: Record<string, string>) => {
  return useQuery({
    queryKey: ['accounting', 'bills', filters ?? {}],
    queryFn: async () => {
      const data = await api.accounting.bills.list(filters);
      // ✅ Validate query response with Zod schema
      const validation = validateEntityArray(billSchema, data);
      if (!validation.valid) {
        logger.error('Invalid bills query response:', validation.errors);
        throw new Error('Invalid bills data received from API');
      }
      return validation.data! as Bill[];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - bills don't change frequently
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
  });
};

export const useReceivables = (filters?: Record<string, string>) => {
  return useQuery({
    queryKey: ['accounting', 'receivables', filters ?? {}],
    queryFn: async () => {
      const data = await api.accounting.receivables.list(filters);
      // ✅ Validate query response with Zod schema
      const validation = validateEntityArray(receivableSchema, data);
      if (!validation.valid) {
        const errorCount = validation.errors?.length || 0;
        const errorDetails = validation.errors?.slice(0, 3).map(({ index, error }) => {
          const rec = data[index];
          const fieldErrors = error.errors.map(e => `${e.path.join('.')}: ${e.message}`).join('; ');
          return `Record ${index} (ID: ${rec?.id || 'unknown'}): ${fieldErrors}`;
        }).join(' | ') || 'Unknown validation errors';

        logger.error('Invalid receivables query response:', {
          queryKey: 'accounting.receivables',
          totalRecords: Array.isArray(data) ? data.length : 0,
          invalidRecords: errorCount,
          errorDetails: errorCount > 3 ? `${errorDetails}... (${errorCount - 3} more)` : errorDetails,
          errors: validation.errors,
        });
        throw new Error('Invalid receivables data received from API');
      }
      return validation.data! as import('types').Receivable[];
    },
    staleTime: 2 * 60 * 1000, // 2 minutes - receivables change more frequently
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * React Query hook to fetch all bank accounts.
 * @returns Query result containing bank accounts array
 */
export const useBankAccounts = () => {
  return useQuery({
    queryKey: ['accounting', 'bankAccounts'],
    queryFn: async () => {
      const data = await api.accounting.bankAccounts.list();
      // ✅ Validate query response with Zod schema
      const validation = validateEntityArray(bankAccountSchema, data);
      if (!validation.valid) {
        logger.error('Invalid bank accounts query response:', validation.errors);
        throw new Error('Invalid bank accounts data received from API');
      }
      return validation.data! as import('types').BankAccount[];
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - bank accounts rarely change
    gcTime: 15 * 60 * 1000, // 15 minutes (formerly cacheTime)
  });
};

export const useJournalEntries = () => {
  return useQuery({
    queryKey: ['accounting', 'journalEntries'],
    queryFn: async () => {
      const data = await api.accounting.journalEntries.list();
      // ✅ Validate query response with Zod schema
      const validation = validateEntityArray(journalEntrySchema, data);
      if (!validation.valid) {
        logger.error('Invalid journal entries query response:', validation.errors);
        throw new Error('Invalid journal entries data received from API');
      }
      return validation.data! as JournalEntry[];
    },
  });
};

/**
 * React Query mutation hook to create a new bill.
 * Automatically invalidates bills query cache and shows toast notification.
 * @returns Mutation object with mutate function
 */
export const useCreateBill = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (newBill: Omit<Bill, 'id' | 'paidDate' | 'createdAt' | 'updatedAt'>) =>
      api.accounting.bills.create(newBill),
    onSuccess: async (data) => {
      // ✅ Validate API response with Zod schema (if data is returned)
      if (data) {
        const validation = validateEntity(billSchema, data);
        if (!validation.valid) {
          logger.error('Invalid bill response from API:', validation.errors);
          showToast('Bill created but received invalid data from server', 'error');
          await queryClient.invalidateQueries({ queryKey: ['accounting', 'bills'] });
          return;
        }
      }
      await queryClient.invalidateQueries({ queryKey: ['accounting', 'bills'] });
      showToast('Bill added to your records', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to create bill', 'error');
    }
  });
};

export const useApproveBill = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async (billId: string) =>
      parseBillMutationResponse(await api.accounting.bills.approve(billId)),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['accounting'] });
      showToast('Bill approved and ready to pay', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to approve bill', 'error');
    }
  });
};

/**
 * React Query mutation hook to pay a bill.
 * Automatically invalidates accounting queries and shows toast notification.
 * @param billId - The ID of the bill to pay
 * @param bankAccountId - The ID of the bank account to use for payment
 * @returns Mutation object with mutate function
 */
export const usePayBill = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async ({ billId, bankAccountId }: { billId: string; bankAccountId: string }) =>
      parseBillMutationResponse(await api.accounting.bills.pay(billId, bankAccountId)),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['accounting'] }),
        queryClient.invalidateQueries({ queryKey: ['accounting', 'journalEntries'] }),
      ]);
      showToast('Payment recorded successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Payment failed', 'error');
    }
  });
};

export const useReceivePayment = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async ({ id, bankId, amount }: { id: string, bankId: string, amount: number }) => {
      // Update Receivable Status - this will trigger journal entry creation in api.ts
      await api.accounting.receivables.update(id, { status: 'Paid', bankId });
      return { id, amount };
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['accounting'] }),
        queryClient.invalidateQueries({ queryKey: ['accounting', 'journalEntries'] }),
      ]);
      showToast('Payment received successfully', 'success');
    }
  });
};

/**
 * React Query mutation hook to distribute funds to an owner.
 * Creates a distribution record for the specified owner.
 * @returns Mutation object with mutate function
 */
export const useDistributeFunds = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async ({ ownerId, amount, _bankId }: { ownerId: string, amount: number, _bankId: string }) =>
      parseDistributionMutationResponse(
        await api.accounting.distributions.create({
          ownerId,
          amount,
          date: new Date().toISOString().split('T')[0],
          period: 'On Demand',
          status: 'Sent',
        }),
      ),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['accounting'] }),
        queryClient.invalidateQueries({ queryKey: ['distributions'] }),
      ]);
      showToast('Distribution recorded successfully', 'success');
    }
  });
};

/**
 * React Query mutation hook to acknowledge a distribution (Owner only).
 * @returns Mutation object with mutate function
 */
export const useAcknowledgeDistribution = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async ({ id, notes }: { id: string; notes?: string }) =>
      parseDistributionMutationResponse(await api.accounting.distributions.acknowledge(id, notes)),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['distributions'] }),
        queryClient.invalidateQueries({ queryKey: ['accounting'] }),
      ]);
      showToast('Distribution acknowledged successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to acknowledge distribution', 'error');
    }
  });
};

export const useApproveDistribution = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async (id: string) =>
      parseDistributionMutationResponse(await api.accounting.distributions.approve(id)),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['distributions'] }),
        queryClient.invalidateQueries({ queryKey: ['accounting'] }),
      ]);
      showToast('Distribution approved', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to approve distribution', 'error');
    }
  });
};

export const useProcessDistribution = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async (id: string) =>
      parseDistributionMutationResponse(await api.accounting.distributions.process(id)),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['distributions'] }),
        queryClient.invalidateQueries({ queryKey: ['accounting'] }),
      ]);
      showToast('Distribution processed', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to process payout', 'error');
    }
  });
};

export const useChartOfAccounts = () => {
  return useQuery({
    queryKey: ['accounting', 'coa'],
    queryFn: async () => {
      const data = await api.accounting.coa.list();
      // ✅ Validate query response with Zod schema
      const validation = validateEntityArray(chartOfAccountSchema, data);
      if (!validation.valid) {
        logger.error('Invalid chart of accounts query response:', validation.errors);
        throw new Error('Invalid chart of accounts data received from API');
      }
      return validation.data! as ChartOfAccount[];
    },
  });
};

/**
 * React Query mutation hook to create a new chart of accounts entry.
 * Automatically invalidates chart of accounts query cache.
 * @returns Mutation object with mutate function
 */
export const useCreateAccount = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (account: ChartOfAccount) => api.accounting.coa.create(account),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['accounting', 'coa'] });
    }
  });
};

export const useUpdateAccount = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<ChartOfAccount> }) =>
      api.accounting.coa.update(id, updates),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['accounting', 'coa'] });
      showToast('Account updated successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to update account', 'error');
    }
  });
};

export const useDeleteAccount = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  return useMutation({
    mutationFn: (id: string) => api.accounting.coa.delete(id),
    onSuccess: async (data) => {
      // ✅ Validate success response
      const validation = validateSuccessResponse(data);
      if (!validation.valid) {
        logger.error('Invalid delete response from API:', validation.errors);
        showToast('Account deleted but received invalid response from server', 'error');
        await queryClient.invalidateQueries({ queryKey: ['accounting', 'coa'] });
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ['accounting', 'coa'] });
      showToast('Account deleted successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to delete account', 'error');
    }
  });
};

/**
 * React Query mutation hook to create a new journal entry.
 * Automatically invalidates journal entries query cache and shows toast notification.
 * @returns Mutation object with mutate function
 */
export const useCreateJournalEntry = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (entry: Omit<JournalEntry, 'id'>) => api.accounting.journalEntries.create(entry),
    onMutate: async (newEntry) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: ['accounting', 'journalEntries'] });

      // Snapshot the previous value
      const previousEntries = queryClient.getQueryData<JournalEntry[]>(['accounting', 'journalEntries']);

      // Optimistically update to the new value
      const optimisticEntry: JournalEntry = {
        ...newEntry,
        id: newClientEntityId(), // Temporary ID (optimistic; must be RFC UUID for API hygiene)
      };

      queryClient.setQueryData<JournalEntry[]>(['accounting', 'journalEntries'], (old = []) => [
        optimisticEntry,
        ...old,
      ]);

      // Return a context object with the snapshotted value
      return { previousEntries };
    },
    onError: (err, newEntry, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousEntries) {
        queryClient.setQueryData(['accounting', 'journalEntries'], context.previousEntries);
      }
      showToast('Failed to post entry.', 'error');
    },
    onSuccess: () => {
      showToast('Journal Entry Posted Successfully.', 'success');
    },
    onSettled: async () => {
      // Always refetch after error or success to ensure consistency
      await queryClient.invalidateQueries({ queryKey: ['accounting', 'journalEntries'] });
    },
  });
};

export const useUpdateJournalEntry = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<JournalEntry> }) => api.accounting.journalEntries.update(id, updates),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['accounting', 'journalEntries'] });
      showToast('Journal Entry Updated Successfully.', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to update entry.', 'error');
    }
  });
};

export const useDeleteJournalEntry = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (id: string) => api.accounting.journalEntries.delete(id),
    onSuccess: async (data) => {
      // ✅ Validate success response
      const validation = validateSuccessResponse(data);
      if (!validation.valid) {
        logger.error('Invalid delete response from API:', validation.errors);
        showToast('Journal entry deleted but received invalid response from server', 'error');
        await queryClient.invalidateQueries({ queryKey: ['accounting', 'journalEntries'] });
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ['accounting', 'journalEntries'] });
      showToast('Journal Entry Deleted Successfully.', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to delete entry.', 'error');
    }
  });
};

export const useDeleteBill = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (id: string) => api.accounting.bills.delete(id),
    onSuccess: async (data) => {
      // ✅ Validate success response
      const validation = validateSuccessResponse(data);
      if (!validation.valid) {
        logger.error('Invalid delete response from API:', validation.errors);
        showToast('Bill deleted but received invalid response from server', 'error');
        await queryClient.invalidateQueries({ queryKey: ['accounting', 'bills'] });
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ['accounting', 'bills'] });
      showToast('Bill deleted successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to delete bill', 'error');
    }
  });
};

export const useDeleteReceivable = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (id: string) => api.accounting.receivables.delete(id),
    onSuccess: async (data) => {
      // ✅ Validate success response
      const validation = validateSuccessResponse(data);
      if (!validation.valid) {
        logger.error('Invalid delete response from API:', validation.errors);
        showToast('Receivable deleted but received invalid response from server', 'error');
        await queryClient.invalidateQueries({ queryKey: ['accounting', 'receivables'] });
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ['accounting', 'receivables'] });
      showToast('Receivable deleted successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to delete receivable', 'error');
    }
  });
};

export const useBankTransactions = (bankAccountId?: string, options?: { explainability?: boolean }) => {
  const explainability = Boolean(options?.explainability);
  return useQuery({
    queryKey: ['accounting', 'bankTransactions', bankAccountId, explainability ? 'ex' : 'plain'],
    queryFn: async () => {
      const data = await api.accounting.bankTransactions.list(bankAccountId, { explainability });
      const validation = validateEntityArray(bankTransactionSchema, data);
      if (!validation.valid) {
        logger.error('Invalid bank transactions query response:', validation.errors);
        throw new Error('Invalid bank transactions data received from API');
      }
      return validation.data!;
    },
    enabled: !!bankAccountId,
  });
};

/** Org-scoped owner statement batches for a calendar month (GET /accounting/statement-batches). */
export const useStatementBatches = (
  params: { operatingOrganizationId: string; yyyyMm: string } | undefined,
  queryOptions?: { enabled?: boolean; refetchOnWindowFocus?: boolean },
) => {
  const orgId = params?.operatingOrganizationId ?? '';
  const yyyyMm = params?.yyyyMm ?? '';
  const enabled =
    queryOptions?.enabled === undefined
      ? Boolean(orgId && yyyyMm)
      : queryOptions.enabled && Boolean(orgId && yyyyMm);
  return useQuery({
    queryKey: ['accounting', 'statementBatches', orgId, yyyyMm],
    enabled,
    refetchOnWindowFocus: queryOptions?.refetchOnWindowFocus,
    queryFn: async () => {
      const raw = await api.accounting.statementBatches.list({
        operatingOrganizationId: orgId,
        yyyyMm,
      });
      const validation = validateEntity(statementBatchListResponseSchema, raw);
      if (!validation.valid) {
        logger.error('Invalid statement batches query response:', validation.errors);
        throw new Error('Invalid statement batches data received from API');
      }
      return validation.data!.batches;
    },
  });
};

/** Org-scoped Plaid + Stripe money-ingestion freshness (GET /banking/cash-collection-health). */
export const useCashCollectionMoneyHealth = (
  operatingOrganizationId: string | undefined,
  queryOptions?: { enabled?: boolean },
) => {
  const enabled =
    queryOptions?.enabled === undefined
      ? Boolean(operatingOrganizationId?.trim())
      : queryOptions.enabled;
  return useQuery({
    queryKey: ['banking', 'cashCollectionHealth', operatingOrganizationId ?? 'none'],
    queryFn: async () => api.banking.getCashCollectionMoneyHealth(operatingOrganizationId!.trim()),
    enabled: enabled && Boolean(operatingOrganizationId?.trim()),
    staleTime: 30 * 1000,
  });
};

export const useImportBankStatement = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async ({
      bankAccountId,
      fileContent,
      fileType,
    }: {
      bankAccountId: string;
      fileContent: string;
      fileType: 'CSV' | 'OFX';
    }) =>
      parseBankTransactionImportResponse(
        await api.accounting.bankTransactions.import(bankAccountId, fileContent, fileType),
      ),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['accounting', 'bankTransactions'] });
      const count = data.transactions.length;
      showToast(`Imported ${count} transactions successfully`, 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to import bank statement', 'error');
    }
  });
};

export const useMatchBankTransaction = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async ({ bankTransactionId, ledgerEntryId }: { bankTransactionId: string; ledgerEntryId: string }) =>
      parseBankTransactionMutationResponse(
        await api.accounting.bankTransactions.match(bankTransactionId, ledgerEntryId),
      ),
    onSuccess: () => {
      showToast('Transaction matched successfully', 'success');
      void queryClient.invalidateQueries({ queryKey: ['accounting', 'bankTransactions'] });
      void queryClient.invalidateQueries({ queryKey: ['accounting', 'journalEntries'] });
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to match transaction', 'error');
    }
  });
};

export const useRunReconciliation = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (bankAccountId: string) => api.accounting.reconciliation.match(bankAccountId),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['accounting', 'bankTransactions'] });
      void queryClient.invalidateQueries({ queryKey: ['accounting', 'journalEntries'] });
      void queryClient.invalidateQueries({ queryKey: ['accounting', 'reconciliation'] });
      const n = data?.matches ?? 0;
      showToast(n > 0 ? `Auto-matched ${n} transaction(s).` : 'Auto-match completed; no new matches.', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to run reconciliation', 'error');
    }
  });
};

export const useCompleteReconciliation = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async ({ bankAccountId, period }: { bankAccountId: string; period: string }) =>
      parseReconciliationCompleteResponse(
        await api.accounting.reconciliation.complete(bankAccountId, period),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['accounting', 'reconciliation'] });
      void queryClient.invalidateQueries({ queryKey: ['accounting', 'bankTransactions'] });
      void queryClient.invalidateQueries({ queryKey: ['accounting', 'journalEntries'] });
      showToast(
        'Bank account period marked reconciled (not company month-end close).',
        'success',
      );
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to complete reconciliation', 'error');
    }
  });
};

export const useReconcileBankAccount = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async ({ bankAccountId, date }: { bankAccountId: string; date: string }) =>
      parseReconciliationCompleteResponse(
        await api.accounting.reconciliation.complete(bankAccountId, date),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['accounting', 'bankTransactions'] });
      void queryClient.invalidateQueries({ queryKey: ['accounting', 'journalEntries'] });
      void queryClient.invalidateQueries({ queryKey: ['accounting', 'reconciliation'] });
      showToast(
        'Bank account period marked reconciled (not company month-end close).',
        'success',
      );
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to reconcile account', 'error');
    }
  });
};

/**
 * React Query mutation hook to create CAM data.
 * Automatically invalidates CAM query cache and shows toast notification.
 * @returns Mutation object with mutate function
 */
export const useCreateCAMData = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (camData: { category: string; budget: number; actual: number; propertyId?: string; period?: string }) =>
      api.accounting.cam.create(camData),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['accounting', 'cam'] });
      showToast('CAM entry created successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to create CAM entry', 'error');
    }
  });
};

/**
 * React Query mutation hook to update CAM data.
 * Automatically invalidates CAM query cache and shows toast notification.
 * @returns Mutation object with mutate function
 */
export const useUpdateCAMData = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: { category?: string; budget?: number; actual?: number } }) =>
      api.accounting.cam.update(id, updates),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['accounting', 'cam'] });
      showToast('CAM entry updated successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to update CAM entry', 'error');
    }
  });
};

/**
 * React Query mutation hook to delete CAM data.
 * Automatically invalidates CAM query cache and shows toast notification.
 * @returns Mutation object with mutate function
 */
export const useDeleteCAMData = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (id: string) => api.accounting.cam.delete(id),
    onSuccess: async (data) => {
      // ✅ Validate success response
      const validation = validateSuccessResponse(data);
      if (!validation.valid) {
        logger.error('Invalid delete response from API:', validation.errors);
        showToast('CAM entry deleted but received invalid response from server', 'error');
        await queryClient.invalidateQueries({ queryKey: ['accounting', 'cam'] });
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ['accounting', 'cam'] });
      showToast('CAM entry deleted successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to delete CAM entry', 'error');
    }
  });
};

// Distribution Hooks
export const useDistributions = () => {
  return useQuery({
    queryKey: ['accounting', 'distributions'],
    queryFn: async () => {
      const data = await api.accounting.distributions.list();
      // ✅ Validate query response with Zod schema
      const validation = validateEntityArray(distributionSchema, data);
      if (!validation.valid) {
        logger.error('Invalid distributions query response:', validation.errors);
        throw new Error('Invalid distributions data received from API');
      }
      return validation.data! as Distribution[];
    },
  });
};

export const useBudgets = () => {
  return useQuery({
    queryKey: ['accounting', 'budgets'],
    queryFn: async () => {
      const data = await api.accounting.budgets.list();
      // ✅ Validate query response with Zod schema
      const validation = validateEntityArray(budgetSchema, data);
      if (!validation.valid) {
        logger.error('Invalid budgets query response:', validation.errors);
        throw new Error('Invalid budgets data received from API');
      }
      return validation.data! as Budget[];
    },
  });
};

export const useDistribution = (id: string | undefined) => {
  return useQuery({
    queryKey: ['accounting', 'distributions', id],
    queryFn: async () => {
      const data = await api.accounting.distributions.get(id!);
      // ✅ Validate query response with Zod schema
      if (data) {
        const validation = validateEntity(distributionSchema, data);
        if (!validation.valid) {
          logger.error('Invalid distribution query response:', validation.errors);
          throw new Error('Invalid distribution data received from API');
        }
        return validation.data! as Distribution;
      }
      return null;
    },
    enabled: !!id,
  });
};

// BILL ATTACHMENTS
export const useBillAttachments = (billId: string | undefined) => {
  return useQuery({
    queryKey: ['accounting', 'bills', billId, 'attachments'],
    queryFn: async () => {
      const data = await api.accounting.bills.attachments.list(billId!);
      // ✅ Validate query response with Zod schema (attachments are FileRecords)
      const validation = validateEntityArray(fileRecordResponseSchema, data);
      if (!validation.valid) {
        logger.error('Invalid bill attachments query response:', validation.errors);
        throw new Error('Invalid bill attachments data received from API');
      }
      return validation.data! as import('@repo/shared-types/files').FileRecord[];
    },
    enabled: !!billId,
  });
};

export const useAddBillAttachment = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ billId, file, description }: { billId: string; file: File; description?: string }) =>
      api.accounting.bills.attachments.add(billId, file, description),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ['accounting', 'bills', data.entityId, 'attachments'] });
      showToast('File attached successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to attach file', 'error');
    }
  });
};

export const useDeleteBillAttachment = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (fileId: string) => api.accounting.bills.attachments.delete(fileId),
    onSuccess: async (data) => {
      // ✅ Validate success response
      const validation = validateSuccessResponse(data);
      if (!validation.valid) {
        logger.error('Invalid delete response from API:', validation.errors);
        showToast('File deleted but received invalid response from server', 'error');
        await queryClient.invalidateQueries({ queryKey: ['accounting', 'bills'] });
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ['accounting', 'bills'] });
      showToast('File deleted successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to delete file', 'error');
    }
  });
};

// Recurring Bills
export const useRecurringBills = () => {
  return useQuery({
    queryKey: ['accounting', 'recurringBills'],
    queryFn: async () => {
      const data = await api.accounting.bills.recurring.list();
      // ✅ Validate query response with Zod schema
      const validation = validateEntityArray(recurringBillSchema, data);
      if (!validation.valid) {
        logger.error('Invalid recurring bills query response:', validation.errors);
        return [] as import('@repo/shared-types/accounting/recurringBills').RecurringBill[];
      }
      return validation.data! as import('@repo/shared-types/accounting/recurringBills').RecurringBill[];
    },
  });
};

export const useRecurringBill = (id: string | undefined) => {
  return useQuery({
    queryKey: ['accounting', 'recurringBills', id],
    queryFn: async () => {
      const data = await api.accounting.bills.recurring.get(id!);
      // ✅ Validate query response with Zod schema
      if (data) {
        const validation = validateEntity(recurringBillSchema, data);
        if (!validation.valid) {
          logger.error('Invalid recurring bill query response:', validation.errors);
          return null;
        }
        return validation.data! as import('@repo/shared-types/accounting/recurringBills').RecurringBill;
      }
      return null;
    },
    enabled: !!id,
  });
};

export const useCreateRecurringBill = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (recurringBill: Omit<RecurringBill, 'id' | 'createdAt' | 'updatedAt'>) =>
      api.accounting.bills.recurring.create(recurringBill),
    onSuccess: async (data) => {
      // ✅ Validate API response with Zod schema
      const validation = validateEntity(recurringBillSchema, data);
      if (!validation.valid) {
        logger.error('Invalid recurring bill response from API:', validation.errors);
        showToast('Recurring bill created but received invalid data from server', 'error');
        await queryClient.invalidateQueries({ queryKey: ['accounting', 'recurringBills'] });
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ['accounting', 'recurringBills'] });
      showToast('Recurring bill created successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to create recurring bill', 'error');
    }
  });
};

export const useUpdateRecurringBill = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<RecurringBill> }) =>
      api.accounting.bills.recurring.update(id, updates),
    onSuccess: async (data) => {
      // ✅ Validate API response with Zod schema
      const validation = validateEntity(recurringBillSchema, data);
      if (!validation.valid) {
        logger.error('Invalid recurring bill response from API:', validation.errors);
        showToast('Recurring bill updated but received invalid data from server', 'error');
        await queryClient.invalidateQueries({ queryKey: ['accounting', 'recurringBills'] });
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ['accounting', 'recurringBills'] });
      showToast('Recurring bill updated successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to update recurring bill', 'error');
    }
  });
};

export const useDeleteRecurringBill = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (id: string) => api.accounting.bills.recurring.delete(id),
    onSuccess: async (data) => {
      // ✅ Validate success response
      const validation = validateSuccessResponse(data);
      if (!validation.valid) {
        logger.error('Invalid delete response from API:', validation.errors);
        showToast('Recurring bill deleted but received invalid response from server', 'error');
        await queryClient.invalidateQueries({ queryKey: ['accounting', 'recurringBills'] });
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ['accounting', 'recurringBills'] });
      showToast('Recurring bill deleted successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to delete recurring bill', 'error');
    }
  });
};

export const useGenerateRecurringBills = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: () => api.accounting.bills.recurring.generate(),
    onSuccess: async (result: RecurringBillGenerationResult) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['accounting', 'recurringBills'] }),
        queryClient.invalidateQueries({ queryKey: ['accounting', 'bills'] }),
      ]);
      showToast(`Generated ${result.generatedBills.length} recurring bills`, 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to generate recurring bills', 'error');
    }
  });
};

// PURCHASE ORDERS
export const usePurchaseOrders = (filters?: { vendorId?: string; propertyId?: string }) => {
  return useQuery({
    queryKey: ['accounting', 'purchaseOrders', filters],
    queryFn: async () => {
      const data = await accountingAPI.purchaseOrders.list(filters);
      // ✅ Validate query response with Zod schema
      const validation = validateEntityArray(purchaseOrderSchema, data);
      if (!validation.valid) {
        logger.error('Invalid purchase orders query response:', validation.errors);
        return [] as PurchaseOrder[];
      }
      return validation.data! as PurchaseOrder[];
    },
  });
};

export const usePurchaseOrder = (id: string | undefined) => {
  return useQuery({
    queryKey: ['accounting', 'purchaseOrders', id],
    queryFn: async () => {
      const data = await accountingAPI.purchaseOrders.get(id!);
      // ✅ Validate query response with Zod schema
      if (data) {
        const validation = validateEntity(purchaseOrderSchema, data);
        if (!validation.valid) {
          logger.error('Invalid purchase order query response:', validation.errors);
          return null;
        }
        return validation.data! as PurchaseOrder;
      }
      return null;
    },
    enabled: !!id,
  });
};

export const useCreatePurchaseOrder = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (po: Omit<PurchaseOrder, 'id' | 'createdAt' | 'updatedAt'>) =>
      accountingAPI.purchaseOrders.create(po),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['accounting', 'purchaseOrders'] });
      showToast('Purchase order created successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to create purchase order', 'error');
    }
  });
};

export const useUpdatePurchaseOrder = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<PurchaseOrder> }) =>
      accountingAPI.purchaseOrders.update(id, updates),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['accounting', 'purchaseOrders'] });
      showToast('Purchase order updated successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to update purchase order', 'error');
    }
  });
};

export const useDeletePurchaseOrder = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (id: string) => accountingAPI.purchaseOrders.delete(id),
    onSuccess: async (data) => {
      // ✅ Validate success response
      const validation = validateSuccessResponse(data);
      if (!validation.valid) {
        logger.error('Invalid delete response from API:', validation.errors);
        showToast('Purchase order deleted but received invalid response from server', 'error');
        await queryClient.invalidateQueries({ queryKey: ['accounting', 'purchaseOrders'] });
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ['accounting', 'purchaseOrders'] });
      showToast('Purchase order deleted successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to delete purchase order', 'error');
    }
  });
};

// 3-WAY MATCHING
export const useThreeWayMatches = (filters?: { purchaseOrderId?: string; invoiceId?: string; status?: string }) => {
  return useQuery({
    queryKey: ['accounting', 'threeWayMatches', filters],
    queryFn: async () => {
      const data = await accountingAPI.threeWayMatches.list(filters);
      const validation = validateEntityArray(threeWayMatchSchema, data);
      if (!validation.valid) {
        logger.error('Invalid three-way matches query response:', validation.errors);
        throw new Error('Invalid three-way matches data received from API');
      }
      return validation.data!;
    },
  });
};

export const useThreeWayMatch = (id: string | undefined) => {
  return useQuery({
    queryKey: ['accounting', 'threeWayMatches', id],
    queryFn: async () => {
      const data = await accountingAPI.threeWayMatches.get(id!);
      const validation = validateEntity(threeWayMatchSchema, data);
      if (!validation.valid) {
        logger.error('Invalid three-way match query response:', validation.errors);
        throw new Error('Invalid three-way match data received from API');
      }
      return validation.data!;
    },
    enabled: !!id,
  });
};

export const usePerformThreeWayMatch = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async ({ poId, invoiceId, receiptId }: { poId: string; invoiceId: string; receiptId?: string }) =>
      parseThreeWayMatchMutationResponse(
        await accountingAPI.threeWayMatches.perform(poId, invoiceId, receiptId),
      ),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['accounting', 'threeWayMatches'] }),
        queryClient.invalidateQueries({ queryKey: ['accounting', 'bills'] }),
      ]);
      showToast('3-way match performed successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to perform 3-way match', 'error');
    }
  });
};

export const useApproveThreeWayMatch = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async (matchId: string) =>
      parseThreeWayMatchMutationResponse(await accountingAPI.threeWayMatches.approve(matchId)),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['accounting', 'threeWayMatches'] }),
        queryClient.invalidateQueries({ queryKey: ['accounting', 'bills'] }),
      ]);
      showToast('Match approved successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to approve match', 'error');
    }
  });
};

export const useFlagMatchDiscrepancy = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async ({ matchId, notes }: { matchId: string; notes: string }) =>
      parseThreeWayMatchMutationResponse(await accountingAPI.threeWayMatches.flagDiscrepancy(matchId, notes)),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['accounting', 'threeWayMatches'] });
      showToast('Discrepancy flagged successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to flag discrepancy', 'error');
    }
  });
};

export const useCheckLateFees = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: () => api.accounting.receivables.checkLateFees(),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['accounting', 'receivables'] });
      showToast(
        `Late fee check completed: ${result.processed} processed`,
        'success'
      );
      return result;
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to check late fees', 'error');
    }
  });
};

export const useBatchTransactions = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (operations: import('types').BatchOperation[]) =>
      api.transactions.batch(operations),
    onSuccess: async (result) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['accounting'] }),
        queryClient.invalidateQueries({ queryKey: ['accounting', 'receivables'] }),
        queryClient.invalidateQueries({ queryKey: ['accounting', 'bills'] }),
      ]);
      showToast(
        `Batch operation completed: ${result.success ? 'Success' : 'Partial success'}`,
        result.success ? 'success' : 'info'
      );
      return result;
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to execute batch operation', 'error');
    }
  });
};

// PAYMENT PLANS
export const usePaymentPlans = (tenantId?: string) => {
  return useQuery({
    queryKey: ['accounting', 'paymentPlans', tenantId],
    queryFn: async () => {
      const data = await accountingAPI.paymentPlans.list(tenantId);
      // ✅ Validate query response with Zod schema
      const validation = validateEntityArray(paymentPlanSchema, data);
      if (!validation.valid) {
        logger.error('Invalid payment plans query response:', validation.errors);
        return [] as import('@repo/shared-types/accounting/paymentPlans').PaymentPlan[];
      }
      return validation.data! as import('@repo/shared-types/accounting/paymentPlans').PaymentPlan[];
    },
  });
};

export const usePaymentPlan = (id: string | undefined) => {
  return useQuery({
    queryKey: ['accounting', 'paymentPlans', id],
    queryFn: async () => {
      const data = await accountingAPI.paymentPlans.get(id!);
      // ✅ Validate query response with Zod schema
      if (data) {
        const validation = validateEntity(paymentPlanSchema, data);
        if (!validation.valid) {
          logger.error('Invalid payment plan query response:', validation.errors);
          return null;
        }
        return validation.data! as import('@repo/shared-types/accounting/paymentPlans').PaymentPlan;
      }
      return null;
    },
    enabled: !!id,
  });
};

export const useCreatePaymentPlan = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (planData: { tenantId: string; receivableId: string; numberOfInstallments: number; startDate: string }) =>
      accountingAPI.paymentPlans.create(planData),
    onSuccess: async (data) => {
      // ✅ Validate API response with Zod schema
      const validation = validateEntity(paymentPlanSchema, data);
      if (!validation.valid) {
        logger.error('Invalid payment plan response from API:', validation.errors);
        showToast('Payment plan created but received invalid data from server', 'error');
        await queryClient.invalidateQueries({ queryKey: ['accounting', 'paymentPlans'] });
        return;
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['accounting', 'paymentPlans'] }),
        queryClient.invalidateQueries({ queryKey: ['accounting', 'receivables'] }),
      ]);
      showToast('Payment plan created successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to create payment plan', 'error');
    }
  });
};

export const useUpdateInstallmentStatus = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ planId, installmentId, status, paidAmount, paidDate }: { planId: string; installmentId: string; status: InstallmentStatus; paidAmount?: number; paidDate?: string }) =>
      accountingAPI.paymentPlans.updateInstallmentStatus(planId, installmentId, status, paidAmount, paidDate),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['accounting', 'paymentPlans'] }),
        queryClient.invalidateQueries({ queryKey: ['accounting', 'receivables'] }),
      ]);
      showToast('Installment status updated successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to update installment status', 'error');
    }
  });
};

/** Linked Plaid items (GET /banking/plaid-items). Pass `operatingOrganizationId` for PM/owner org-scoped feeds. */
export const usePlaidItems = (operatingOrganizationId?: string, queryOptions?: { enabled?: boolean }) => {
  const enabled =
    queryOptions?.enabled === undefined
      ? Boolean(operatingOrganizationId?.trim())
      : queryOptions.enabled;
  return useQuery({
    queryKey: ['banking', 'plaidItems', operatingOrganizationId ?? 'tenant'],
    queryFn: async () => api.banking.listPlaidItems(operatingOrganizationId),
    enabled,
    staleTime: 60 * 1000,
  });
};

/** Plaid accounts for one linked item (GET /banking/plaid-accounts). */
export const usePlaidAccountsForItem = (
  plaidItemRowId: string | undefined,
  operatingOrganizationId?: string,
  queryOptions?: { enabled?: boolean },
) => {
  const enabled =
    queryOptions?.enabled === undefined
      ? Boolean(plaidItemRowId?.trim())
      : queryOptions.enabled;
  return useQuery({
    queryKey: ['banking', 'plaidAccounts', plaidItemRowId, operatingOrganizationId ?? 'tenant'],
    queryFn: async () => api.banking.listPlaidAccounts(plaidItemRowId!, operatingOrganizationId),
    enabled,
    staleTime: 60 * 1000,
  });
};

export const useMapPlaidAccount = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async (body: PlaidAccountMapBody) => api.banking.mapPlaidAccount(body),
    onSuccess: async (_data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['banking', 'plaidItems'] }).catch(() => {
          /* cache refresh best-effort */
        }),
        queryClient.invalidateQueries({ queryKey: ['banking', 'plaidAccounts', variables.plaidItemId] }).catch(() => {
          /* cache refresh best-effort */
        }),
      ]);
      showToast('Ledger mapping updated', 'success');
    },
    onError: (error: unknown) => {
      showToast(getFriendlyErrorMessage(error) || 'Failed to update mapping', 'error');
    },
  });
};

const financialCloseBlockerSchema = z.object({
  code: z.string(),
  severity: z.enum(['Critical', 'High']),
  message: z.string(),
  route: z.string().optional(),
});

export type FinancialCloseBlocker = z.infer<typeof financialCloseBlockerSchema>;

/** Server Financial Critical|High signals that block month-end seal (see periodLockService). */
export const useFinancialCloseBlockers = () => {
  return useQuery({
    queryKey: ['accounting', 'periods', 'financialBlockers'],
    queryFn: async () => {
      const listFn = accountingAPI.periods?.listFinancialBlockers;
      if (!listFn) {
        return [] as FinancialCloseBlocker[];
      }
      const data = await listFn();
      const validation = validateEntityArray(financialCloseBlockerSchema, data);
      if (!validation.valid) {
        logger.error('Invalid financial close blockers query response:', validation.errors);
        throw new Error('Invalid financial close blockers data received from API');
      }
      return validation.data as FinancialCloseBlocker[];
    },
    staleTime: 60 * 1000,
  });
};

